import React, {useEffect, useState} from "react";
import {Alert, Box, Button, CircularProgress, Paper, Typography} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import {useTranslation} from "react-i18next";
import useCourierTracking from "./use-courier-tracking";
import CourierMapCanvas from "./courier-map-canvas";
import {trackingState} from "./tracking-state";
import {deliveryProgress} from "./delivery-progress";
import CourierLocationCard from "./courier-location-card";

export default function CourierTrackingPanel({courierId, status, onDetails, onFilterCourier, filterLabel = "CourierTrackingFilter"}) {
    const {t} = useTranslation();
    const filterScope = `${courierId}:${status}`;
    const [pagination, setPagination] = useState({scope: filterScope, page: 0});
    const page = pagination.scope === filterScope ? pagination.page : 0;
    useEffect(() => setPagination({scope: filterScope, page: 0}), [filterScope]);
    const {data, loading, error, allowed, identity, refresh} = useCourierTracking({courierId, status, page});
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        // The age clock deliberately keeps running during network outages and hidden-page polling pauses.
        const interval = setInterval(() => setNow(previous => Math.max(previous, Date.now())), 1000);
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        if (data && page > Math.max(0, data.totalPages - 1)) setPagination({scope: filterScope, page: Math.max(0, data.totalPages - 1)});
    }, [data, page, filterScope]);
    if (!allowed) return null;
    const points = (data?.content || []).filter(item => !status || item.deliveryStatuses?.includes(status))
        .map(item => ({...item, ...trackingState(item, Math.max(now, Date.now())),
            progress: (item.deliveryStatuses || []).map(value => t(deliveryProgress(value).label)).join(", ")}));
    return <Paper component="section" variant="outlined" aria-label={t("CourierMapTitle")} sx={{my: 2, p: {xs: 1, sm: 2}, minWidth: 0}}>
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1}}>
            <Typography variant="h6">{t("CourierMapTitle")}</Typography>
            <Button variant="outlined" startIcon={<Refresh/>} aria-label={t("CourierMapRefresh")}
                onClick={refresh} disabled={loading}>{t("Refresh")}</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>{t("CourierMapPageHint")}</Typography>
        {error && <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{t("Retry")}</Button>}>{t(error)}</Alert>}
        {loading && !data && <CircularProgress size={24} aria-label={t("CourierMapLoading")}/>}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(260px, 1fr)"}, gap: 2}}>
            <CourierMapCanvas key={identity} points={points} onDetails={onDetails}/>
            <Box sx={{minWidth: 0}}>
                {!loading && !points.length && <Typography role="status">{t("CourierMapEmpty")}</Typography>}
                {!!points.length && !points.some(point => point.location) && <Typography role="status">{t("CourierMapNoPoints")}</Typography>}
                <Box component="ul" aria-label={t("CourierMapSummary")} sx={{display: "grid", gap: 1.5, listStyle: "none",
                    px: {xs: 0, md: 1}, pb: {xs: 0, md: 1}, pt: 0, m: 0,
                    maxHeight: {xs: 420, md: 560}, overflow: "auto", alignContent: "start"}}>
                    {points.map(point => <CourierLocationCard key={point.courierId} point={point} onDetails={onDetails}
                        onFilterCourier={onFilterCourier} filterLabel={filterLabel}/>)}
                </Box>
            </Box>
        </Box>
        <Box component="nav" aria-label={t("CourierMapPages")} sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
            <Button aria-label={t("CourierMapPrevious")} disabled={page === 0 || loading}
                onClick={() => setPagination({scope: filterScope, page: page - 1})}>{t("CourierMapPreviousShort")}</Button>
            <Typography variant="body2">{t("CourierMapPage", {page: page + 1, pages: Math.max(1, data?.totalPages || 0)})}</Typography>
            <Button aria-label={t("CourierMapNext")} disabled={!data || page + 1 >= data.totalPages || loading}
                onClick={() => setPagination({scope: filterScope, page: page + 1})}>{t("CourierMapNextShort")}</Button>
        </Box>
    </Paper>;
}
